/*
 * Zodiac motion — scroll and interaction choreography for the zodiac-* sections, ported from the
 * reference build (zodiac.qodebites.com). Runs on GSAP (assets/zodiac-gsap.js, loaded just before).
 *
 * Every section root carries data-z-motion="<name>"; the matching controller below runs inside a
 * gsap.context, so it can be reverted cleanly (theme editor reloads, breakpoint changes).
 * Elements marked data-z-hide start hidden (see zodiac.css) and are revealed by their controller.
 */

const REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ---------- Background videos ----------
   Videos from snippets/zodiac-video download nothing until they're played: each one plays while
   it's on screen and pauses when it leaves (a hidden mobile/desktop variant never counts as on
   screen). The category showcase plays its own, only the active slide's. This runs before GSAP
   is needed, so the videos play even if the animations can't start. */
const videoObserver = !REDUCE && 'IntersectionObserver' in window
  ? new IntersectionObserver((entries) => {
    entries.forEach(({ target, isIntersecting }) => {
      if (!isIntersecting) return target.pause();
      if (target.closest('[data-media-paused]')) return; // paused by the visitor (coming-soon page)
      target.muted = true;
      target.play().catch(() => {});
    });
  }, { rootMargin: '200px 0px' })
  : null;
const watchVideos = (scope) => {
  if (!videoObserver) return;
  scope.querySelectorAll('video[data-zodiac-video]').forEach((video) => {
    if (!video.closest('[data-z-motion="showcase"]')) videoObserver.observe(video);
  });
};
watchVideos(document);
document.addEventListener('shopify:section:load', (e) => watchVideos(e.target));
document.addEventListener('shopify:section:unload', (e) => {
  e.target.querySelectorAll('video[data-zodiac-video]').forEach((video) => videoObserver?.unobserve(video));
});

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
for (let i = 0; i < 200 && !(window.gsap && window.ScrollTrigger && window.SplitText); i += 1) await wait(25);

const { gsap, ScrollTrigger, SplitText, DrawSVGPlugin, Observer } = window;
const root = document.documentElement;
if (!gsap || !ScrollTrigger || !SplitText) {
  root.classList.remove('z-motion');
  throw new Error('Zodiac motion: GSAP did not load');
}
gsap.registerPlugin(ScrollTrigger, SplitText, DrawSVGPlugin, Observer);
window.ZodiacMotion = true;

const EDITOR = Boolean(window.Shopify && window.Shopify.designMode);
const CLIP_LEFT = 'inset(0% 100% 0% 0% round 24px)';
const CLIP_FULL = 'inset(0% 0% 0% 0% round 24px)';
const show = (targets) => gsap.set(targets, { visibility: 'visible' });
const splitLines = (el) => SplitText.create(el, { type: 'lines', mask: 'lines' });

/* ---------- Scroller ----------
   Horizon scrolls the page inside .page-wrapper on desktop (html and body are locked there), and
   the window everywhere else. ScrollTrigger and the scroll lock follow whichever one is in use. */
const pageWrapper = document.querySelector('.page-wrapper');
const detectScroller = () => {
  if (pageWrapper && /(auto|scroll)/.test(getComputedStyle(pageWrapper).overflowY) &&
      getComputedStyle(document.body).overflowY === 'hidden') return pageWrapper;
  return window;
};
let scroller = detectScroller();
ScrollTrigger.defaults({ scroller });
const getScroll = () => (scroller === window ? window.scrollY : scroller.scrollTop);
const setScroll = (y) => scroller.scrollTo({ top: y, behavior: 'instant' });
const scrollerTop = () => (scroller === window ? 0 : scroller.getBoundingClientRect().top);
const pageTop = (el) => Math.round(getScroll() + el.getBoundingClientRect().top - scrollerTop());

/* ---------- Lifecycle ---------- */
const controllers = {};
const mounted = new Set();

function mount(section) {
  const run = controllers[section.dataset.zMotion];
  if (!run) return;
  unmount(section);
  try {
    section.__zodiac = gsap.context((self) => run(section, self), section);
    mounted.add(section);
  } catch (error) {
    console.error('Zodiac motion:', section.dataset.zMotion, error);
    section.__zodiac?.revert();
    section.querySelectorAll('[data-z-hide]').forEach((el) => (el.style.visibility = 'visible'));
  }
}

function unmount(section) {
  section.__zodiac?.revert();
  section.__zodiac = null;
  mounted.delete(section);
}

function mountAll(scope = document) {
  const sections = scope.matches?.('[data-z-motion]') ? [scope] : [...scope.querySelectorAll('[data-z-motion]')];
  sections.forEach(mount);
  ScrollTrigger.refresh();
}

/* ---------- Magnetic buttons (desktop) ----------
   Buttons with data-z-magnetic lean towards the cursor when it comes within 40px, and spring back. */
function initMagnetic() {
  if (REDUCE || !window.matchMedia('(hover: hover)').matches) return;
  const STRENGTH = 0.35;
  const near = new WeakSet();
  let frame = 0;
  let event = null;
  const update = () => {
    frame = 0;
    document.querySelectorAll('[data-z-magnetic]').forEach((btn) => {
      const inner = btn.querySelector('.z-btn__inner');
      const rect = btn.getBoundingClientRect();
      const cx = rect.left + rect.width / 2 - gsap.getProperty(btn, 'x');
      const cy = rect.top + rect.height / 2 - gsap.getProperty(btn, 'y');
      const dx = event.clientX - cx;
      const dy = event.clientY - cy;
      const inside = Math.abs(dx) < rect.width / 2 + 40 && Math.abs(dy) < rect.height / 2 + 40;
      if (inside) {
        gsap.to(btn, { x: dx * STRENGTH, y: dy * STRENGTH, duration: 0.4, ease: 'power3.out', overwrite: true });
        gsap.to(inner, { x: dx * STRENGTH * 0.5, y: dy * STRENGTH * 0.5, duration: 0.4, ease: 'power3.out', overwrite: true });
        near.add(btn);
      } else if (near.has(btn)) {
        gsap.to([btn, inner], { x: 0, y: 0, duration: 1.2, ease: 'elastic.out(1, 0.3)', overwrite: true });
        near.delete(btn);
      }
    });
  };
  window.addEventListener('mousemove', (e) => {
    event = e;
    if (!frame) frame = requestAnimationFrame(update);
  }, { passive: true });
}

/* ---------- Hero: slides push sideways with a parallax background ---------- */
controllers.hero = (section) => {
  const slides = gsap.utils.toArray('[data-slide]', section);
  const fills = gsap.utils.toArray('[data-fill]', section);
  const segments = gsap.utils.toArray('[data-go]', section);
  const count = slides.length;
  if (!count) return undefined;
  const DURATION = REDUCE ? 0 : 1.2;
  const EASE = 'power3.inOut';
  const autoplay = section.dataset.autoplay === 'true' && count > 1 && !REDUCE;
  const interval = Number(section.dataset.interval) || 5;
  const state = { current: 0, busy: false, held: false };
  let progress = null;

  const reveal = (slide, delay) => {
    const items = slide.querySelectorAll('[data-reveal]');
    const ctas = slide.querySelectorAll('[data-cta]');
    if (REDUCE) {
      gsap.set([...items, ...ctas], { autoAlpha: 1, y: 0, clearProps: 'clipPath' });
      return gsap.timeline();
    }
    return gsap.timeline()
      .fromTo(items, { y: 40, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 0.9, ease: 'power3.out', stagger: 0.12, delay })
      .fromTo(ctas, { clipPath: CLIP_LEFT, autoAlpha: 1 }, { clipPath: CLIP_FULL, duration: 0.9, ease: EASE, clearProps: 'clipPath' }, delay + 0.5);
  };

  const mark = (index) => {
    segments.forEach((seg, i) => seg.setAttribute('aria-current', String(i === index)));
    slides.forEach((slide, i) => (slide.inert = i !== index));
  };

  const startProgress = (index) => {
    progress?.kill();
    gsap.set(fills, { scaleX: 0 });
    if (!autoplay || state.held) {
      gsap.set(fills[index], { scaleX: 1 });
      return;
    }
    progress = gsap.to(fills[index], {
      scaleX: 1,
      duration: interval,
      ease: 'none',
      onComplete: () => go((state.current + 1) % count, 1),
    });
  };

  const go = (to, dir) => {
    if (state.busy || to === state.current) return;
    state.busy = true;
    progress?.kill();
    const from = slides[state.current];
    const next = slides[to];
    const fromBg = from.querySelector('[data-bg]');
    const nextBg = next.querySelector('[data-bg]');
    gsap.set(next, { xPercent: 100 * dir, zIndex: 2 });
    gsap.set(nextBg, { xPercent: -30 * dir });
    gsap.set(next.querySelectorAll('[data-reveal]'), { autoAlpha: 0, y: 40 });
    gsap.set(next.querySelectorAll('[data-cta]'), { clipPath: CLIP_LEFT, autoAlpha: 1 });
    mark(to);
    startProgress(to);
    gsap.timeline({
      defaults: { duration: DURATION, ease: EASE },
      onComplete: () => {
        gsap.set(from, { xPercent: 100, zIndex: 0 });
        gsap.set(fromBg, { xPercent: 0 });
        gsap.set(next, { zIndex: 1 });
        state.current = to;
        state.busy = false;
      },
    })
      .to(from, { xPercent: -100 * dir }, 0)
      .to(fromBg, { xPercent: 30 * dir }, 0)
      .to(next, { xPercent: 0 }, 0)
      .to(nextBg, { xPercent: 0 }, 0)
      .add(reveal(next, 0), DURATION * 0.55);
  };

  gsap.set(slides, { xPercent: 100, zIndex: 0 });
  gsap.set(slides[0], { xPercent: 0, zIndex: 1 });
  slides.slice(1).forEach((slide) => gsap.set(slide.querySelectorAll('[data-reveal], [data-cta]'), { autoAlpha: 0 }));
  mark(0);
  reveal(slides[0], 0.2);
  startProgress(0);

  const step = (dir) => go((state.current + dir + count) % count, dir);
  const onClick = (e) => {
    const prev = e.target.closest('[data-prev]');
    const next = e.target.closest('[data-next]');
    const seg = e.target.closest('[data-go]');
    if (prev) step(-1);
    else if (next) step(1);
    else if (seg) {
      const i = Number(seg.dataset.go);
      if (i !== state.current) go(i, i > state.current ? 1 : -1);
    }
  };
  let touch = null;
  const onDown = (e) => {
    if (e.pointerType !== 'mouse') touch = { x: e.clientX, y: e.clientY };
  };
  const onUp = (e) => {
    if (!touch) return;
    const dx = e.clientX - touch.x;
    const dy = e.clientY - touch.y;
    touch = null;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) step(dx < 0 ? 1 : -1);
  };
  const onSelect = (e) => {
    const i = slides.indexOf(e.target.closest('[data-slide]'));
    state.held = true;
    if (i > -1 && i !== state.current) go(i, i > state.current ? 1 : -1);
    else startProgress(state.current);
  };
  const onDeselect = () => {
    state.held = false;
    startProgress(state.current);
  };
  section.addEventListener('click', onClick);
  section.addEventListener('pointerdown', onDown);
  section.addEventListener('pointerup', onUp);
  section.addEventListener('shopify:block:select', onSelect);
  section.addEventListener('shopify:block:deselect', onDeselect);
  return () => {
    progress?.kill();
    slides.forEach((slide) => (slide.inert = false));
    section.removeEventListener('click', onClick);
    section.removeEventListener('pointerdown', onDown);
    section.removeEventListener('pointerup', onUp);
    section.removeEventListener('shopify:block:select', onSelect);
    section.removeEventListener('shopify:block:deselect', onDeselect);
  };
};

/* ---------- Statement: typed eyebrow, words rising into place ---------- */
controllers.statement = (section) => {
  const eyebrow = section.querySelector('[data-eyebrow]');
  const para = section.querySelector('[data-para]');
  const cta = section.querySelectorAll('[data-cta]');
  if (REDUCE) return undefined;
  const trigger = { trigger: section, start: 'top 75%', toggleActions: 'play none none reverse' };
  if (eyebrow) {
    SplitText.create(eyebrow, {
      type: 'chars',
      autoSplit: true,
      onSplit: (split) => {
        show(eyebrow);
        return gsap.from(split.chars, { autoAlpha: 0, duration: 0.01, stagger: 0.08, ease: 'none', scrollTrigger: trigger });
      },
    });
  }
  if (para) {
    SplitText.create(para, {
      type: 'lines,words',
      mask: 'lines',
      autoSplit: true,
      onSplit: (split) => {
        show(para);
        return gsap.from(split.words, {
          yPercent: 120,
          rotate: 6,
          transformOrigin: '0% 100%',
          duration: 1.2,
          ease: 'power4.out',
          stagger: 0.06,
          delay: 0.5,
          scrollTrigger: trigger,
        });
      },
    });
  }
  if (cta.length) {
    gsap.fromTo(cta, { clipPath: CLIP_LEFT, visibility: 'visible' }, {
      clipPath: CLIP_FULL,
      duration: 1,
      ease: 'power3.inOut',
      delay: 1.8,
      clearProps: 'clipPath',
      scrollTrigger: trigger,
    });
  }
  return undefined;
};

/* ---------- Category showcase ----------
   Full-screen categories. When the section reaches the top of the screen it holds the page and the
   wheel/swipe moves between categories (the next one wipes up from the bottom); past the last one
   the page scrolls on. In the theme editor, or with "Scroll through" off, it auto-rotates instead. */
controllers.showcase = (section) => {
  const slides = gsap.utils.toArray('[data-slide]', section);
  const count = slides.length;
  if (!count) return undefined;
  const LAST = count - 1;
  const SENSITIVITY = 0.6;
  const SETTLE = REDUCE ? 0 : 1;
  const OVERSHOOT = 0.08;
  const lockEnabled = section.dataset.lock === 'true' && !EDITOR;
  const q = (i, sel) => slides[i].querySelectorAll(sel);
  const bar = section.querySelector('[data-progress]');
  const state = { raw: 0, pos: 0 };
  let locked = false;
  let shown = null;
  let fromBelow = false;
  let lockedAt = 0;
  let canEnter = true;
  let releasedAt = 0;
  let inView = false;
  const timelines = {};

  const outro = (i) => gsap.timeline()
    .to(q(i, '[data-line], [data-num]'), { yPercent: -110, duration: 0.5, ease: 'power2.in', stagger: 0.05 }, 0)
    .to(q(i, '[data-pill], [data-card]'), { autoAlpha: 0, y: -20, duration: 0.4, ease: 'power2.in' }, 0)
    .to(q(i, '[data-cta]'), { autoAlpha: 0, duration: 0.3 }, 0);
  const intro = (i) => gsap.timeline()
    .fromTo(q(i, '[data-pill]'), { autoAlpha: 0, y: 20 }, { autoAlpha: 1, y: 0, duration: 0.7, ease: 'power3.out' }, 0)
    .fromTo(q(i, '[data-num]'), { yPercent: 110 }, { yPercent: 0, duration: 1, ease: 'power4.out' }, 0)
    .fromTo(q(i, '[data-line]'), { yPercent: 110 }, { yPercent: 0, duration: 1, ease: 'power4.out', stagger: 0.12 }, 0.1)
    .fromTo(q(i, '[data-cta]'), { autoAlpha: 1, clipPath: CLIP_LEFT }, { clipPath: CLIP_FULL, duration: 0.9, ease: 'power3.inOut' }, 0.45)
    .fromTo(q(i, '[data-card]'), { autoAlpha: 0, y: 80 }, { autoAlpha: 1, y: 0, duration: 1, ease: 'power3.out' }, 0.5);

  const showContent = (i) => {
    if (shown === i) return;
    if (shown !== null) {
      timelines[shown]?.kill();
      timelines[shown] = outro(shown);
      if (REDUCE) timelines[shown].progress(1);
    }
    shown = i;
    timelines[i]?.kill();
    if (i === 0) {
      gsap.set(q(0, '[data-pill], [data-card], [data-cta]'), { autoAlpha: 1, y: 0, clearProps: 'clipPath' });
      gsap.set(q(0, '[data-line], [data-num]'), { yPercent: 0 });
    } else {
      timelines[i] = intro(i);
      if (REDUCE) timelines[i].progress(1);
    }
  };

  const videos = gsap.utils.toArray('video[data-zodiac-video]', section);
  const syncVideos = (visible) => {
    videos.forEach((video) => {
      const slide = video.closest('[data-slide]');
      const index = slides.indexOf(slide);
      const play = inView && !REDUCE && (index === -1 || visible.includes(index)) && getComputedStyle(video).display !== 'none';
      if (play && video.paused) video.play().catch(() => {});
      else if (!play && !video.paused) video.pause();
    });
  };

  const render = () => {
    const p = gsap.utils.clamp(0, LAST, state.pos);
    const i = Math.min(Math.floor(p), LAST);
    const f = p - i;
    const moving = f > 0.001 && i < LAST;
    slides.forEach((slide, k) => {
      const on = k === i || (moving && k === i + 1);
      gsap.set(slide, {
        autoAlpha: on ? 1 : 0,
        zIndex: k === i + 1 ? 2 : Number(k === i),
        clipPath: moving && k === i + 1 ? `inset(${(1 - f) * 100}% 0% 0% 0%)` : 'none',
      });
    });
    gsap.set(q(i, '[data-outer], [data-inner]'), { yPercent: 0 });
    if (moving) {
      const next = slides[i + 1];
      gsap.set(next.querySelectorAll('[data-outer]'), { yPercent: (1 - f) * 100 });
      gsap.set(next.querySelectorAll('[data-inner]'), { yPercent: -(1 - f) * 100 });
      gsap.set(next.querySelectorAll('[data-img]'), { yPercent: (1 - f) * 8 });
      gsap.set(q(i, '[data-img]'), { yPercent: -15 * f });
    } else {
      gsap.set(q(i, '[data-outer], [data-inner], [data-img]'), { yPercent: 0 });
    }
    let active = Math.round(p);
    if (moving) {
      const heading = slides[i + 1].querySelector('h2');
      const top = heading ? heading.getBoundingClientRect().top - section.getBoundingClientRect().top : section.offsetHeight / 2;
      active = (1 - f) * section.offsetHeight <= top ? i + 1 : i;
    }
    showContent(active);
    gsap.set(bar, { scaleX: (p + 1) / count });
    syncVideos(moving ? [i, i + 1] : [i]);
  };

  const reset = (p) => {
    gsap.killTweensOf(state);
    state.raw = p;
    state.pos = p;
    shown = null;
    gsap.set(section.querySelectorAll('[data-pill], [data-card], [data-cta]'), { autoAlpha: 0 });
    gsap.set(section.querySelectorAll('[data-line], [data-num]'), { yPercent: 110 });
    render();
  };

  const settle = () => gsap.to(state, {
    pos: gsap.utils.clamp(0, LAST, state.raw),
    duration: SETTLE,
    ease: 'power3.out',
    overwrite: true,
    onUpdate: render,
    onComplete: render,
  });

  reset(0);

  const view = new IntersectionObserver(([entry]) => {
    inView = entry.isIntersecting;
    render();
  });
  view.observe(section);

  const cleanups = [() => view.disconnect()];

  if (lockEnabled) {
    let pendingRefresh = false;
    const release = () => {
      releasedAt = performance.now();
      locked = false;
      observer.disable();
      state.raw = gsap.utils.clamp(0, LAST, state.raw);
      settle();
      if (pendingRefresh) {
        pendingRefresh = false;
        setTimeout(() => !locked && ScrollTrigger.refresh(), 400);
      }
    };
    const observer = Observer.create({
      target: window,
      type: 'wheel,touch',
      wheelSpeed: -1,
      preventDefault: true,
      onChange: (self) => {
        const delta = -self.deltaY;
        if (!delta) return;
        state.raw += (delta / window.innerHeight) * SENSITIVITY;
        if (state.raw < -OVERSHOOT || state.raw > LAST + OVERSHOOT) {
          canEnter = state.raw < 0;
          release();
          return;
        }
        state.raw = gsap.utils.clamp(-OVERSHOOT, LAST + OVERSHOOT, state.raw);
        settle();
      },
    });
    observer.disable();

    const lock = () => {
      locked = true;
      lockedAt = performance.now();
      setScroll(pageTop(section));
      observer.enable();
    };

    const onScroll = () => {
      const y = getScroll();
      const top = pageTop(section);
      if (locked && Math.abs(y - top) > 1) setScroll(top);
      else if (!locked && !canEnter && performance.now() - releasedAt > 800 && y < top - 40) lock();
      if (!locked && !canEnter && y < top - window.innerHeight * 0.6) canEnter = true;
    };
    scroller.addEventListener('scroll', onScroll, { passive: true });

    const trigger = ScrollTrigger.create({
      trigger: section,
      start: 'top top',
      end: 'bottom top',
      onEnter: () => {
        if (!canEnter || performance.now() - lockedAt < 600 || locked || getScroll() >= trigger.end) return;
        canEnter = false;
        fromBelow = false;
        reset(0);
        lock();
      },
      onEnterBack: () => {
        fromBelow = true;
        reset(LAST);
      },
      onLeave: () => {
        fromBelow = false;
      },
      onLeaveBack: () => {
        if (!fromBelow) return;
        fromBelow = false;
        lock();
      },
    });
    if (trigger.isActive) lock();

    let resizeTimer;
    const resize = new ResizeObserver(() => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => (locked ? (pendingRefresh = true) : ScrollTrigger.refresh()), 150);
    });
    resize.observe(document.body);

    cleanups.push(() => {
      observer.kill();
      scroller.removeEventListener('scroll', onScroll);
      clearTimeout(resizeTimer);
      resize.disconnect();
    });
  } else if (count > 1) {
    // Auto-rotate (storefront with "Scroll through" off); the theme editor waits for block selection.
    const interval = (Number(section.dataset.interval) || 6) * 1000;
    let timer = null;
    const next = () => {
      const target = Math.round(state.pos) + 1;
      if (target > LAST) reset(0);
      else {
        state.raw = target;
        settle();
      }
      timer = setTimeout(next, interval);
    };
    if (!EDITOR && !REDUCE) timer = setTimeout(next, interval);
    cleanups.push(() => clearTimeout(timer));
  }

  const onSelect = (e) => {
    const i = slides.indexOf(e.target.closest('[data-slide]'));
    if (i < 0) return;
    state.raw = i;
    settle();
  };
  section.addEventListener('shopify:block:select', onSelect);
  cleanups.push(() => section.removeEventListener('shopify:block:select', onSelect));

  return () => cleanups.forEach((fn) => fn());
};

/* ---------- Series: living line background, line-by-line reveals, boat opening from the middle ---------- */
function linesBackground(section, svg) {
  const W = 1512;
  const H = 982;
  const N = 44;
  const smooth = (t) => t * t * (3 - 2 * t);
  const lerp = (a, b, t) => a + (b - a) * t;
  const lines = Array.from({ length: N }, (_, k) => {
    const n = k / (N - 1);
    const start = { x: 0, y: 110 + n ** 1.15 * 780 };
    const split = 0.55;
    const end = n < split ? { x: W, y: 440 + (n / split) * 502 } : { x: W * (1 - ((n - split) / (1 - split)) * 0.93), y: H };
    const o = smooth(n);
    const c1 = { x: start.x + lerp(W * 0.2, W * 0.1, o), y: start.y + lerp(H * 0.3, H * 0.32, o) };
    const c2 = { x: lerp(end.x - W * 0.5, end.x - W * 0.06, o), y: lerp(end.y + 6 + n * 40, end.y - H * 0.36, o) };
    const grey = Math.round(lerp(233, 246, n));
    return { p: [start, c1, c2, end], phase: k * 0.32, amp: 10 + 14 * o, color: `rgb(${grey},${grey},${grey})` };
  });
  const paths = Array.from(svg.querySelectorAll('path'));
  paths.forEach((path, k) => lines[k] && path.setAttribute('stroke', lines[k].color));

  const mouse = { x: W / 2, y: H / 2, s: 0 };
  const toX = gsap.quickTo(mouse, 'x', { duration: 0.9, ease: 'power3.out' });
  const toY = gsap.quickTo(mouse, 'y', { duration: 0.9, ease: 'power3.out' });
  const onMove = (e) => {
    const r = svg.getBoundingClientRect();
    const k = Math.max(r.width / W, r.height / H);
    toX((e.clientX - r.left - (r.width - W * k) / 2) / k);
    toY((e.clientY - r.top - (r.height - H * k) / 2) / k);
    gsap.to(mouse, { s: 1, duration: 0.6, overwrite: 'auto' });
  };
  const onLeave = () => gsap.to(mouse, { s: 0, duration: 1.4, ease: 'power2.out', overwrite: 'auto' });
  const pull = (pt) => {
    const dx = mouse.x - pt.x;
    const dy = mouse.y - pt.y;
    const f = Math.exp(-((dx * dx + dy * dy) / 115600)) * 0.38 * mouse.s;
    return { x: pt.x + dx * f, y: pt.y + dy * f };
  };
  let active = true;
  const draw = () => {
    if (!active) return;
    const t = REDUCE ? 0 : gsap.ticker.time;
    lines.forEach((line, k) => {
      const a = Math.sin(t * 0.55 + line.phase) * line.amp;
      const b = Math.cos(t * 0.45 + line.phase + 1.3) * line.amp;
      const [p0, c1, c2, p3] = line.p;
      const d1 = pull({ x: c1.x + a * 0.7, y: c1.y + a });
      const d2 = pull({ x: c2.x + b, y: c2.y - b * 0.8 });
      paths[k]?.setAttribute('d', `M${p0.x} ${p0.y}C${d1.x.toFixed(1)} ${d1.y.toFixed(1)} ${d2.x.toFixed(1)} ${d2.y.toFixed(1)} ${p3.x} ${p3.y}`);
    });
  };
  draw();
  if (REDUCE) return () => {};
  section.addEventListener('pointermove', onMove);
  section.addEventListener('pointerleave', onLeave);
  gsap.ticker.add(draw);
  ScrollTrigger.create({ trigger: svg, start: 'top bottom', end: 'bottom top', onToggle: (self) => (active = self.isActive) });
  return () => {
    gsap.ticker.remove(draw);
    section.removeEventListener('pointermove', onMove);
    section.removeEventListener('pointerleave', onLeave);
  };
}

controllers.series = (section) => {
  const svg = section.querySelector('[data-lines]');
  const stopLines = svg ? linesBackground(section, svg) : () => {};
  if (REDUCE) return stopLines;

  const heads = gsap.utils.toArray('[data-head], [data-tab-label]', section);
  const headLines = heads.map(splitLines).flatMap((s) => s.lines);
  gsap.set(headLines, { yPercent: 110 });
  show(heads);

  let entered = false;
  let panelSplits = [];
  const activePanel = () => section.querySelector('[data-panel]:not([hidden])');
  const hidePanels = () => gsap.set(section.querySelectorAll('[data-panel] [data-reveal], [data-panel] [data-boat], [data-panel] [data-cta-wrap]'), { visibility: 'hidden' });

  const revealPanel = (delay = 0) => {
    const panel = activePanel();
    if (!panel) return;
    panelSplits.forEach((s) => s.revert());
    const items = gsap.utils.toArray('[data-reveal]', panel);
    show(items);
    panelSplits = items.map(splitLines);
    gsap.from(panelSplits.flatMap((s) => s.lines), { yPercent: 110, duration: 1, ease: 'power4.out', stagger: 0.08, delay });
    gsap.fromTo(panel.querySelectorAll('[data-cta-wrap]'), { autoAlpha: 1, clipPath: CLIP_LEFT }, { clipPath: CLIP_FULL, duration: 1, ease: 'power3.inOut', delay: delay + 0.5, clearProps: 'clipPath' });
    gsap.fromTo(panel.querySelectorAll('[data-boat]'), { autoAlpha: 1, clipPath: 'inset(50% 0% 50% 0%)' }, { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.6, ease: 'power3.inOut', delay });
    gsap.fromTo(panel.querySelectorAll('[data-boat] img, [data-boat] svg'), { scale: 1.25 }, { scale: 1, duration: 1.8, ease: 'power3.out', delay });
  };

  ScrollTrigger.create({
    trigger: section,
    start: 'top 65%',
    onEnter: () => {
      entered = true;
      gsap.to(headLines, { yPercent: 0, duration: 1, ease: 'power4.out', stagger: 0.06 });
      revealPanel(0.5);
    },
    onLeaveBack: () => {
      entered = false;
      gsap.set(headLines, { yPercent: 110 });
      hidePanels();
    },
  });

  const onTab = () => entered && revealPanel(0);
  section.addEventListener('zodiac:tab', onTab);
  return () => {
    stopLines();
    section.removeEventListener('zodiac:tab', onTab);
    panelSplits.forEach((s) => s.revert());
  };
};

/* ---------- Highlights: layered parallax ---------- */
controllers.stats = (section) => {
  if (REDUCE) return undefined;
  const mm = gsap.matchMedia();
  mm.add({ mobile: '(max-width: 639px)', wide: '(min-width: 640px)' }, (ctx) => {
    const mobile = ctx.conditions.mobile;
    const trigger = { trigger: section, start: 'top bottom', end: 'bottom top', scrub: 1 };
    gsap.fromTo(section.querySelectorAll('[data-bg]'), { yPercent: -9 }, { yPercent: 9, ease: 'none', scrollTrigger: trigger });
    if (!mobile) gsap.fromTo(section.querySelectorAll('[data-text]'), { y: 50 }, { y: -50, ease: 'none', scrollTrigger: trigger });
    gsap.utils.toArray('[data-card]', section).forEach((card) => {
      gsap.set(card, { rotation: mobile ? Number(card.dataset.rot) || 0 : 0 });
      const amp = (Number(card.dataset.amp) || 0) * (mobile ? 1.5 : 1);
      gsap.fromTo(card, { y: amp }, { y: -amp, ease: 'none', scrollTrigger: trigger });
    });
  }, section);
  return () => mm.revert();
};

/* ---------- News: rows reveal (image wipes up and settles, then title and pill) ---------- */
controllers.news = (section) => {
  if (REDUCE) return undefined;
  const mm = gsap.matchMedia();
  mm.add({ mobile: '(max-width: 639px)', wide: '(min-width: 640px)' }, (ctx) => {
    const groups = ctx.conditions.mobile ? [section.querySelector('[data-cards]')] : gsap.utils.toArray('[data-row]', section);
    groups.filter(Boolean).forEach((group) => {
      const posts = gsap.utils.toArray('[data-post]', group);
      const sel = gsap.utils.selector(group);
      const titles = sel('[data-post-title]');
      gsap.set(sel('[data-post-img]'), { clipPath: 'inset(100% 0% 0% 0% round 24px)' });
      gsap.set(sel('[data-post-img] img, [data-post-img] svg'), { scale: 1.25 });
      gsap.set(titles, { yPercent: 110 });
      gsap.set(sel('[data-post-cta]'), { clipPath: CLIP_LEFT });
      const tl = gsap.timeline({ scrollTrigger: { trigger: group, start: 'top 80%', toggleActions: 'play none none reverse' } });
      posts.forEach((post, k) => {
        const at = k * 0.18;
        tl.to(post.querySelectorAll('[data-post-img]'), { clipPath: 'inset(0% 0% 0% 0% round 24px)', duration: 1.2, ease: 'power3.inOut' }, at)
          .to(post.querySelectorAll('[data-post-img] img, [data-post-img] svg'), { scale: 1, duration: 1.6, ease: 'power3.out' }, at)
          .to(post.querySelectorAll('[data-post-title]'), { yPercent: 0, duration: 0.9, ease: 'power4.out' }, at + 0.6)
          .to(post.querySelectorAll('[data-post-cta]'), { clipPath: CLIP_FULL, duration: 0.9, ease: 'power3.inOut', clearProps: 'clipPath' }, at + 0.8);
      });
    });
  }, section);
  return () => mm.revert();
};

/* ---------- Showrooms: zoomable, draggable map ---------- */
// Robinson projection fitted to the map shape (assets/zodiac-world-land.svg, 1512 × 800).
const ROBINSON_X = [1, 0.9986, 0.9954, 0.99, 0.9822, 0.973, 0.96, 0.9427, 0.9216, 0.8962, 0.8679, 0.835, 0.7986, 0.7597, 0.7186, 0.6732, 0.6213, 0.5722, 0.5322];
const ROBINSON_Y = [0, 0.062, 0.124, 0.186, 0.248, 0.31, 0.372, 0.434, 0.4958, 0.5571, 0.6176, 0.6769, 0.7346, 0.7903, 0.8435, 0.8936, 0.9394, 0.9761, 1];
const robinson = (table, lat) => {
  const a = Math.min(90, Math.abs(lat)) / 5;
  const i = Math.min(17, Math.floor(a));
  return table[i] + (table[i + 1] - table[i]) * (a - i);
};
const project = (lat, lng) => [756.25 + 4.2435 * robinson(ROBINSON_X, lat) * lng, 450.13 - 398.01 * Math.sign(lat) * robinson(ROBINSON_Y, lat)];

controllers.showrooms = (section) => {
  const map = section.querySelector('[data-map]');
  const group = section.querySelector('[data-transform]');
  const land = section.querySelector('[data-land-path]');
  const pins = gsap.utils.toArray('[data-pin]', section);
  const dealers = gsap.utils.toArray('[data-dealer]', section);
  const MW = 1512;
  const MH = 800;
  // Coordinates are free text: accept "55,6573" (decimal comma) and "55.6573, 12.4736" pasted into
  // the latitude field (the second number is then used as the longitude when that field is empty).
  const numbers = (value) => (String(value || '').replace(/(\d),(\d)/g, '$1.$2').match(/-?\d+(?:\.\d+)?/g) || []).map(Number);
  const coords = pins.map((pin) => {
    const lat = numbers(pin.dataset.lat);
    const lng = numbers(pin.dataset.lng);
    return project(lat[0] ?? 0, lng[0] ?? lat[1] ?? 0);
  });
  const view = { s: 1, x: 0, y: 0 };
  const fit = { k: 1, ox: 0, oy: 0, w: 1, h: 1 };
  let selected = gsap.utils.clamp(0, Math.max(0, pins.length - 1), Number(section.dataset.selected) || 0);
  const focusX = (w) => (w < 640 ? w / 2 : (543 + w) / 2);
  const focusY = (w) => (w < 640 ? 0.34 : 0.45);

  const apply = () => {
    const { s, x, y } = view;
    const { k, ox, oy } = fit;
    group.setAttribute('transform', `translate(${x} ${y}) scale(${s})`);
    const z = gsap.utils.clamp(0, 1, (s - 1) / 3);
    pins.forEach((pin, i) => {
      const [mx, my] = coords[i];
      pin.style.left = `${ox + (mx * s + x) * k}px`;
      pin.style.top = `${oy + (my * s + y) * k}px`;
      const zoom = pin.querySelector('[data-pin-zoom]');
      if (zoom) zoom.style.transform = `scale(${0.45 + 0.55 * z})`;
      const label = pin.querySelector('[data-pin-label]');
      if (label) label.style.opacity = i === selected ? '1' : String(gsap.utils.clamp(0, 1, (z - 0.4) / 0.4));
    });
  };
  const clampView = (s, x, y) => {
    const { k, ox, oy, w, h } = fit;
    return {
      x: gsap.utils.clamp((w - ox) / k - MW * s, -ox / k, x),
      y: gsap.utils.clamp((h - oy) / k - MH * s, -oy / k, y),
    };
  };
  const animateTo = (s, x, y) => {
    const c = clampView(s, x, y);
    gsap.to(view, { s, x: c.x, y: c.y, duration: REDUCE ? 0 : 1.6, ease: 'power3.inOut', overwrite: true, onUpdate: apply });
  };
  const centreOn = (i, s) => {
    const [mx, my] = coords[i] || [MW / 2, MH / 2];
    const { k, ox, oy, w, h } = fit;
    return [(focusX(w) - ox) / k - mx * s, (h * focusY(w) - oy) / k - my * s];
  };
  const zoomBy = (factor) => {
    const { s, x, y } = view;
    const { k, ox, oy, w, h } = fit;
    const next = gsap.utils.clamp(1, 9, s * factor);
    if (next === 1) return animateTo(1, 0, 0);
    const cx = (w / 2 - ox) / k;
    const cy = (h / 2 - oy) / k;
    return animateTo(next, cx - (cx - x) * (next / s), cy - (cy - y) * (next / s));
  };
  const cardIn = () => {
    const dealer = dealers.find((d) => !d.hidden);
    if (!dealer || REDUCE) return;
    gsap.fromTo(dealer.querySelectorAll('[data-card-item]'), { autoAlpha: 0, y: 14 }, { autoAlpha: 1, y: 0, duration: 0.7, ease: 'power3.out', stagger: 0.07, overwrite: 'auto' });
  };
  const select = (i) => {
    selected = i;
    pins.forEach((pin, j) => {
      pin.classList.toggle('is-selected', j === i);
      pin.querySelector('[data-pin-btn]')?.setAttribute('aria-pressed', String(j === i));
    });
    dealers.forEach((d) => (d.hidden = Number(d.dataset.dealer) !== i));
    apply();
    animateTo(4.5, ...centreOn(i, 4.5));
    cardIn();
  };
  const measure = () => {
    const r = map.getBoundingClientRect();
    const k = Math.max(r.width / MW, r.height / MH);
    Object.assign(fit, { k, ox: (r.width - MW * k) / 2, oy: (r.height - MH * k) / 2, w: r.width, h: r.height });
    const c = clampView(view.s, view.x, view.y);
    view.x = c.x;
    view.y = c.y;
    apply();
  };

  measure();
  {
    const s = 2.6;
    const c = clampView(s, ...centreOn(selected, s));
    Object.assign(view, { s, x: c.x, y: c.y });
    apply();
  }
  const resize = new ResizeObserver(measure);
  resize.observe(map);

  // Load the land shape when the section gets close.
  const loader = new IntersectionObserver(([entry]) => {
    if (!entry.isIntersecting) return;
    loader.disconnect();
    fetch(section.dataset.land)
      .then((r) => r.text())
      .then((svg) => {
        const d = svg.match(/ d="([^"]+)"/);
        if (d) land.setAttribute('d', d[1]);
      })
      .catch(() => {});
  }, { rootMargin: '800px 0px' });
  loader.observe(section);

  // Drag to pan
  let drag = null;
  const onDown = (e) => {
    if (e.target.closest('button, a, [data-loc-ui]')) return;
    gsap.killTweensOf(view);
    drag = { id: e.pointerId, sx: e.clientX, sy: e.clientY, vx: view.x, vy: view.y };
    map.setPointerCapture(e.pointerId);
    map.classList.add('is-dragging');
  };
  const onMove = (e) => {
    if (!drag || drag.id !== e.pointerId) return;
    const c = clampView(view.s, drag.vx + (e.clientX - drag.sx) / fit.k, drag.vy + (e.clientY - drag.sy) / fit.k);
    view.x = c.x;
    view.y = c.y;
    apply();
  };
  const onUp = (e) => {
    if (drag?.id !== e.pointerId) return;
    drag = null;
    map.classList.remove('is-dragging');
  };
  const onClick = (e) => {
    const pin = e.target.closest('[data-pin-btn]');
    const zoom = e.target.closest('[data-zoom]');
    const stepBtn = e.target.closest('[data-step]');
    if (pin) select(Number(pin.closest('[data-pin]').dataset.pin));
    else if (zoom) zoomBy(zoom.dataset.zoom === 'in' ? 1.6 : 1 / 1.6);
    else if (stepBtn && pins.length) select((selected + Number(stepBtn.dataset.step) + pins.length) % pins.length);
  };
  const onSelect = (e) => {
    const pin = e.target.closest('[data-pin]');
    if (pin) select(Number(pin.dataset.pin));
  };
  map.addEventListener('pointerdown', onDown);
  map.addEventListener('pointermove', onMove);
  map.addEventListener('pointerup', onUp);
  map.addEventListener('pointercancel', onUp);
  section.addEventListener('click', onClick);
  section.addEventListener('shopify:block:select', onSelect);

  if (!REDUCE) {
    const titleEl = section.querySelector('[data-loc-title]');
    const title = titleEl ? splitLines(titleEl) : null;
    if (titleEl) show(titleEl);
    if (title) gsap.set(title.lines, { yPercent: 110 });
    gsap.set(section.querySelectorAll('[data-land]'), { autoAlpha: 0 });
    gsap.set(section.querySelectorAll('[data-pin-inner]'), { scale: 0, transformOrigin: '50% 100%' });
    gsap.set(section.querySelectorAll('[data-loc-ui]'), { autoAlpha: 0, y: 24 });
    gsap.timeline({ scrollTrigger: { trigger: section, start: 'top 70%', toggleActions: 'play none none reverse' } })
      .to(title ? title.lines : [], { yPercent: 0, duration: 1, ease: 'power4.out', stagger: 0.1 })
      .to(section.querySelectorAll('[data-land]'), { autoAlpha: 1, duration: 1.4, ease: 'power2.out' }, 0.1)
      .to(section.querySelectorAll('[data-pin-inner]'), { scale: 1, duration: 0.7, ease: 'back.out(1.8)', stagger: 0.12 }, 0.8)
      .to(section.querySelectorAll('[data-loc-ui]'), { autoAlpha: 1, y: 0, duration: 0.9, ease: 'power3.out', stagger: 0.1 }, 0.6);
  }

  return () => {
    resize.disconnect();
    loader.disconnect();
    map.removeEventListener('pointerdown', onDown);
    map.removeEventListener('pointermove', onMove);
    map.removeEventListener('pointerup', onUp);
    map.removeEventListener('pointercancel', onUp);
    section.removeEventListener('click', onClick);
    section.removeEventListener('shopify:block:select', onSelect);
  };
};

/* ---------- Banner: parallax media, line reveal ---------- */
controllers.cta = (section) => {
  if (REDUCE) return undefined;
  const frame = section.querySelector('[data-cta-frame]');
  gsap.fromTo(section.querySelectorAll('[data-cta-video]'), { yPercent: -8 }, {
    yPercent: 8,
    ease: 'none',
    scrollTrigger: { trigger: frame, start: 'top bottom', end: 'bottom top', scrub: 1 },
  });
  const texts = gsap.utils.toArray('[data-cta-text]', section);
  const lines = texts.map(splitLines).flatMap((s) => s.lines);
  show(texts);
  gsap.set(lines, { yPercent: 110 });
  const buttons = section.querySelectorAll('[data-cta-btn]');
  gsap.set(buttons, { clipPath: CLIP_LEFT, visibility: 'visible' });
  gsap.timeline({ scrollTrigger: { trigger: frame, start: 'top 60%', toggleActions: 'play none none reverse' } })
    .to(lines, { yPercent: 0, duration: 1, ease: 'power4.out', stagger: 0.1 })
    .to(buttons, { clipPath: CLIP_FULL, duration: 1, ease: 'power3.inOut', clearProps: 'clipPath' }, '-=0.4');
  return undefined;
};

/* ---------- Footer: drawings drawn on scroll, logo assembling, text rising ---------- */
controllers.footer = (section) => {
  if (REDUCE) return undefined;
  const mobile = window.innerWidth < 768;
  const scrub = { trigger: section, start: 'top 85%', end: mobile ? 'bottom bottom' : 'top 15%', scrub: 1 };
  const art = gsap.utils.toArray('[data-draw] path', section);
  if (art.length) gsap.fromTo(art, { drawSVG: '0%' }, { drawSVG: '100%', ease: 'none', stagger: 0.04, scrollTrigger: scrub });
  const rules = gsap.utils.toArray('[data-rule]', section);
  if (rules.length) gsap.fromTo(rules, { drawSVG: '0%' }, { drawSVG: '100%', ease: 'none', scrollTrigger: scrub });

  const lines = gsap.utils.toArray('[data-foot-text]', section)
    .filter((el) => el.offsetParent !== null)
    .map(splitLines)
    .flatMap((s) => s.lines);
  gsap.set(lines, { yPercent: 110 });
  gsap.timeline({ scrollTrigger: { trigger: section, start: 'top 55%', toggleActions: 'play none none reverse' } })
    .to(lines, { yPercent: 0, duration: 0.9, ease: 'power4.out', stagger: 0.04 }, 0)
    .fromTo(gsap.utils.toArray('[data-foot-social]', section), { autoAlpha: 0, y: 16 }, { autoAlpha: 1, y: 0, duration: 0.7, ease: 'power3.out', stagger: 0.07 }, 0.5);

  const parts = gsap.utils.toArray('[data-logo-part]', section);
  const logo = section.querySelector('[data-logo]');
  if (!parts.length || !logo) return undefined;
  const [icon, ...letters] = parts;
  gsap.set(letters, { clipPath: 'inset(0% 100% 0% 0%)' });
  gsap.set(icon, { rotate: -200, scale: 0.2, autoAlpha: 0, transformOrigin: '50% 50%' });
  gsap.timeline({ scrollTrigger: { trigger: logo, start: 'top 85%', toggleActions: 'play none none reverse' } })
    .to(icon, { rotate: 0, scale: 1, autoAlpha: 1, duration: 1.4, ease: 'back.out(1.4)' })
    .to(letters, { clipPath: 'inset(0% 0% 0% 0%)', duration: 0.9, ease: 'power3.inOut', stagger: 0.12 }, 0.4);
  const jiggle = () => gsap.fromTo(letters, { x: 0 }, { x: 10, duration: 0.25, ease: 'power2.out', stagger: { each: 0.05, yoyo: true, repeat: 1 }, overwrite: 'auto' });
  logo.addEventListener('mouseenter', jiggle);
  return () => logo.removeEventListener('mouseenter', jiggle);
};

/* ---------- Coming soon: the footer's choreography, played on arrival ----------
   The background settles, the emblem spins in while the letters wipe in, the rule draws across,
   then the text rises and the form and socials fade up. */
controllers.soon = (section) => {
  const logo = section.querySelector('[data-logo]');
  const parts = gsap.utils.toArray('[data-logo-part]', section);
  const [icon, ...letters] = parts;
  const panelItems = (panel) => gsap.utils.toArray('[data-panel-item]', panel);
  const activePanel = () => section.querySelector('[data-panel].is-active');
  const cleanups = [];

  // Items of the hidden panel follow their panel's visibility.
  gsap.set(gsap.utils.toArray('[data-panel]:not(.is-active) [data-panel-item]', section), { visibility: 'inherit' });

  if (REDUCE) {
    gsap.set(section.querySelectorAll('[data-z-hide]'), { visibility: 'inherit' });
    return undefined;
  }

  // Opacity only: hiding the items (visibility) would take focus away from the field just focused.
  const onPanel = (e) => {
    const shown = panelItems(e.detail.panel);
    gsap.set(shown, { visibility: 'inherit' });
    gsap.fromTo(shown, { opacity: 0, y: 16 }, { opacity: 1, y: 0, duration: 0.7, ease: 'power3.out', stagger: 0.07, overwrite: 'auto' });
  };
  section.addEventListener('zodiac:panel', onPanel);
  cleanups.push(() => section.removeEventListener('zodiac:panel', onPanel));

  const texts = gsap.utils.toArray('[data-soon-text]', section);
  const lines = texts.map(splitLines).flatMap((s) => s.lines);
  gsap.set(lines, { yPercent: 110 });
  show(texts);

  const rules = gsap.utils.toArray('[data-rule]', section);
  gsap.set(rules, { drawSVG: '0%' });
  show(section.querySelectorAll('.z-soon__rule'));

  const items = [...panelItems(activePanel()), ...section.querySelectorAll('[data-foot-social], [data-soon-ui]')];
  gsap.set(items, { autoAlpha: 0, y: 16 });

  const image = logo?.querySelector('.z-soon__logo-img');
  if (icon) {
    gsap.set(letters, { clipPath: 'inset(0% 100% 0% 0%)' });
    gsap.set(icon, { rotate: -200, scale: 0.2, autoAlpha: 0, transformOrigin: '50% 50%' });
  } else if (image) {
    gsap.set(image, { autoAlpha: 0, scale: 0.9 });
  }
  if (logo) show(logo);

  const tl = gsap.timeline({ delay: 0.15 })
    .fromTo(section.querySelectorAll('[data-soon-bg]'), { scale: 1.12 }, { scale: 1, duration: 2.6, ease: 'power3.out' }, 0);
  if (icon) {
    tl.to(icon, { rotate: 0, scale: 1, autoAlpha: 1, duration: 1.4, ease: 'back.out(1.4)' }, 0.35)
      .to(letters, { clipPath: 'inset(0% 0% 0% 0%)', duration: 0.9, ease: 'power3.inOut', stagger: 0.12 }, 0.75);
  } else if (image) {
    tl.to(image, { autoAlpha: 1, scale: 1, duration: 1.2, ease: 'power3.out' }, 0.35);
  }
  tl.to(rules, { drawSVG: '100%', duration: 1.2, ease: 'power3.inOut' }, 1.1)
    .to(lines, { yPercent: 0, duration: 0.9, ease: 'power4.out', stagger: 0.06 }, 1.3)
    .to(items, { autoAlpha: 1, y: 0, duration: 0.7, ease: 'power3.out', stagger: 0.07 }, 1.6);
  // After a wrong password the field has autofocus, which the browser skips while it's hidden.
  const autofocus = activePanel()?.querySelector('[autofocus]');
  if (autofocus) tl.call(() => autofocus.focus({ preventScroll: true }), null, 1.9);

  if (logo && letters.length) {
    const jiggle = () => gsap.fromTo(letters, { x: 0 }, { x: 10, duration: 0.25, ease: 'power2.out', stagger: { each: 0.05, yoyo: true, repeat: 1 }, overwrite: 'auto' });
    logo.addEventListener('mouseenter', jiggle);
    cleanups.push(() => logo.removeEventListener('mouseenter', jiggle));
  }
  return () => cleanups.forEach((fn) => fn());
};

/* ---------- Start ---------- */
await Promise.race([document.fonts ? document.fonts.ready : Promise.resolve(), wait(1500)]);
mountAll();
initMagnetic();
window.addEventListener('load', () => ScrollTrigger.refresh());

document.addEventListener('shopify:section:load', (e) => mountAll(e.target));
document.addEventListener('shopify:section:unload', (e) => {
  e.target.querySelectorAll('[data-z-motion]').forEach(unmount);
});

// Horizon switches between page-wrapper scrolling (desktop) and window scrolling at 990px.
window.matchMedia('(min-width: 990px)').addEventListener('change', () => {
  const next = detectScroller();
  if (next === scroller) return;
  [...mounted].forEach(unmount);
  scroller = next;
  ScrollTrigger.defaults({ scroller });
  mountAll();
});
